import { signal, useSignal } from "@preact/signals";
import type { ComponentChildren } from "preact";
import { useEffect, useRef } from "preact/hooks";
import { t } from "@/i18n";
import type { Question } from "@/math";
import { lang } from "@/state/store";
import { renderPrompt } from "./katex";

/** The text in the typed-answer box; module-level so the calculator can fill it in. */
export const answerDraft = signal("");
let answerInputEl: HTMLInputElement | null = null;

export function setAnswerDraft(value: string): void {
  answerDraft.value = value;
}

export function focusAnswerInput(): void {
  answerInputEl?.focus();
}

export interface QuestionViewProps {
  question: Question;
  onAnswer: (input: string | number) => void;
  /** Optional countdown; `now` must be refreshed by the parent for the bar to animate. */
  timer?: { startedAt: number; ms: number; now: number };
  /** Multiple-choice indices removed by a hint. */
  eliminated?: number[];
  /** Hint text revealed for typed questions. */
  hint?: string;
  /** Extra action buttons rendered under the answer area. */
  children?: ComponentChildren;
}

/** Prompt + answer input shared by battles and training. */
export function QuestionView({
  question: q,
  onAnswer,
  timer,
  eliminated = [],
  hint,
  children,
}: QuestionViewProps) {
  const L = lang.value;

  // number keys 1–4 pick a multiple-choice answer
  useEffect(() => {
    if (q.answer.mode !== "choice4") return;
    const onKey = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement) return;
      if (/^[1-4]$/.test(e.key) && !eliminated.includes(Number(e.key) - 1)) onAnswer(Number(e.key) - 1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [q.id, onAnswer, eliminated]);

  let timerBar: ComponentChildren = null;
  if (timer && timer.ms > 0) {
    const remaining = Math.max(0, timer.ms - (timer.now - timer.startedAt));
    const pct = (remaining / timer.ms) * 100;
    timerBar = (
      <div class={`timer ${pct < 25 ? "urgent" : ""}`}>
        <div class="fill" style={{ width: `${pct}%` }} />
        <span>{Math.ceil(remaining / 1000)} s</span>
      </div>
    );
  }

  return (
    <div class="question">
      {timerBar}
      <div class="prompt" dangerouslySetInnerHTML={{ __html: renderPrompt(q.prompt[L]) }} />
      {hint && <div class="hint-text" dangerouslySetInnerHTML={{ __html: "💡 " + renderPrompt(hint) }} />}
      {q.answer.mode === "choice4" ? (
        <div class="choices">
          {q.answer.choices.map((c, i) => {
            const out = eliminated.includes(i);
            return (
              <button
                key={q.id + i}
                class={out ? "eliminated" : ""}
                disabled={out}
                onClick={() => onAnswer(i)}
              >
                <kbd>{i + 1}</kbd> {c[L]}
              </button>
            );
          })}
        </div>
      ) : (
        <TypedAnswer
          questionId={q.id}
          unit={q.answer.mode === "numeric" ? q.answer.unit?.[L] : undefined}
          onSubmit={onAnswer}
        />
      )}
      {children && <div class="battle-actions">{children}</div>}
    </div>
  );
}

function TypedAnswer({
  questionId,
  unit,
  onSubmit,
}: {
  questionId: string;
  unit?: string;
  onSubmit: (v: string) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const shown = useSignal(false);

  useEffect(() => {
    answerDraft.value = "";
    answerInputEl = inputRef.current;
    inputRef.current?.focus();
    shown.value = true;
    return () => {
      if (answerInputEl === inputRef.current) answerInputEl = null;
    };
  }, [questionId]);

  const submit = () => {
    if (answerDraft.value.trim() === "") return;
    onSubmit(answerDraft.value);
  };
  const press = (k: string) => {
    if (k === "⌫") answerDraft.value = answerDraft.value.slice(0, -1);
    else answerDraft.value += k;
    inputRef.current?.focus();
  };

  return (
    <div class="typed">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <input
          ref={inputRef}
          type="text"
          inputMode="decimal"
          autoComplete="off"
          placeholder={t("battle.typeAnswer")}
          value={answerDraft.value}
          onInput={(e) => (answerDraft.value = (e.currentTarget as HTMLInputElement).value)}
          onKeyDown={(e) => {
            // Submit on the input itself and stop the event, otherwise the same key press would also
            // trigger "continue" on the feedback that appears right after.
            if (e.key === "Enter") {
              e.preventDefault();
              e.stopPropagation();
              submit();
            }
          }}
        />
        {unit && <span class="unit">{unit}</span>}
        <button type="submit" class="primary">
          {t("battle.answer")}
        </button>
      </form>
      <div class="numpad">
        {["7", "8", "9", "4", "5", "6", "1", "2", "3", "-", "0", ",", "/", "⌫"].map((k) => (
          <button key={k} type="button" onClick={() => press(k)}>
            {k}
          </button>
        ))}
      </div>
    </div>
  );
}

export interface ExplanationViewProps {
  correctAnswerText: string;
  explanation: string[];
}

/** "Correct answer was … / how to solve it" block shown after a wrong answer. */
export function ExplanationView({ correctAnswerText, explanation }: ExplanationViewProps) {
  return (
    <>
      <div class="correct-answer">{t("battle.correctWas", { a: correctAnswerText })}</div>
      <div class="howto">
        <div class="howto-title">{t("battle.howTo")}</div>
        <ol>
          {explanation.map((line, i) => (
            <li key={i} dangerouslySetInnerHTML={{ __html: renderPrompt(line) }} />
          ))}
        </ol>
      </div>
    </>
  );
}
